import { useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { cn } from '@/core/lib/utils';
import { whiteboardFormSchema } from '../schemas';
import type { WhiteboardSummaryDto } from '../types';

const MAX_NAME_LENGTH = 120;

interface WhiteboardTabProps {
  whiteboard: WhiteboardSummaryDto;
  isActive: boolean;
  canManage: boolean;
  /** The last tab cannot be closed — a board with no whiteboards has nothing to show. */
  canDelete: boolean;
  onSelect: (whiteboardId: string) => void;
  onRename: (whiteboardId: string, name: string) => void;
  onDelete: (whiteboard: WhiteboardSummaryDto) => void;
}

/**
 * One whiteboard tab, in the shape of a spreadsheet sheet tab.
 *
 * @remarks
 * The title is edited in place — double-click, or a single click on the tab that is already
 * active, matching how sheet tabs behave. Enter and blur commit, Escape reverts.
 *
 * A name that fails validation is reverted rather than reported: there is nowhere to put an
 * error message on a tab, and the rule is one people discover by trying (a name cannot be
 * empty), not one worth interrupting them over.
 */
export function WhiteboardTab({
  whiteboard,
  isActive,
  canManage,
  canDelete,
  onSelect,
  onRename,
  onDelete,
}: WhiteboardTabProps) {
  const { t } = useTranslation('whiteboards');
  const [isEditing, setEditing] = useState(false);
  const [draft, setDraft] = useState(whiteboard.name ?? '');

  const id = whiteboard.id ?? '';

  function startEditing() {
    if (!canManage) return;
    setDraft(whiteboard.name ?? '');
    setEditing(true);
  }

  function commit() {
    setEditing(false);
    const parsed = whiteboardFormSchema.safeParse({ name: draft });
    if (!parsed.success || parsed.data.name === whiteboard.name) return;
    onRename(id, parsed.data.name);
  }

  if (isEditing) {
    return (
      <Input
        autoFocus
        value={draft}
        maxLength={MAX_NAME_LENGTH}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            setEditing(false);
          }
        }}
        className="h-8 w-40 shrink-0 rounded-b-none text-sm"
        aria-label={t('tabs.rename')}
        data-testid={`whiteboards-tab-input-${id}`}
      />
    );
  }

  return (
    <div
      className={cn(
        'group flex shrink-0 items-center gap-1 rounded-t-md border border-b-0 px-3 transition-colors',
        isActive
          ? 'border-border bg-background font-medium text-foreground'
          : 'border-transparent bg-muted/50 text-muted-foreground hover:text-foreground',
      )}
      data-testid={`whiteboards-tab-${id}`}
    >
      <button
        type="button"
        className="max-w-40 truncate py-1.5 text-sm"
        onClick={() => (isActive ? startEditing() : onSelect(id))}
        onDoubleClick={startEditing}
        data-testid={`whiteboards-tab-select-${id}`}
      >
        {whiteboard.name}
      </button>

      {canManage && canDelete && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'size-5 shrink-0 rounded-sm text-muted-foreground hover:text-destructive',
                !isActive && 'opacity-0 group-hover:opacity-100',
              )}
              aria-label={t('tabs.delete')}
              onClick={() => onDelete(whiteboard)}
              data-testid={`whiteboards-tab-delete-${id}`}
            >
              <X className="size-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('tabs.delete')}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
